import React, { useMemo } from 'react';
import { NavLink, Outlet, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { permissionsApi } from '../../../api/client';

/**
 * AcdIndexRedirect — used as the index route under /acd.
 *
 * Redirects to the first tab the user actually has permission to see, in
 * preferred order (floor first for supervisors/admins, personal first for
 * agents who only have acd_personal). Previously this was a hardcoded
 * Navigate to="floor" which dumped agents into a forbidden page they could
 * never reach.
 */
export function AcdIndexRedirect() {
  const { data: perms, isLoading } = useQuery({
    queryKey: ['permissions', 'me'],
    queryFn: () => permissionsApi.me(),
    staleTime: 60_000,
  });
  if (isLoading) return null;
  const allowed = perms?.is_admin
    ? new Set(['acd_floor','acd_queues','acd_agent_state','acd_personal','acd_supervisor','acd_trunks'])
    : new Set(perms?.modules || []);
  const order = [
    ['acd_floor',       'floor'],
    ['acd_queues',      'queues'],
    ['acd_agent_state', 'agent-state'],
    ['acd_trunks',      'trunks'],
    ['acd_personal',    'personal'],
    ['acd_supervisor',  'supervisor'],
  ];
  const target = order.find(([mod]) => allowed.has(mod));
  // Fallback: send them to /softphone (matches RoleGuard fallback default).
  return <Navigate to={target ? target[1] : '/softphone'} replace />;
}

/**
 * ACD sub-nav. Tab visibility is driven by the user's allowed modules from
 * /api/permissions/me, NOT by their nominal role. This way the admin can
 * grant a viewer access to acd_floor only and the Queues / Personal tabs
 * stay hidden.
 */
const TABS = [
  { to: 'floor',       label: 'Wallboard',        module: 'acd_floor' },
  { to: 'queues',      label: 'Queues',           module: 'acd_queues' },
  { to: 'agent-state', label: 'Agent State',      module: 'acd_agent_state' },
  { to: 'trunks',      label: 'Trunks',           module: 'acd_trunks' },
  { to: 'personal',   label: 'Agent Personal',   module: 'acd_personal' },
  { to: 'supervisor', label: 'Supervisor Tools', module: 'acd_supervisor' },
];

export default function AcdLayout() {
  const { data: perms } = useQuery({
    queryKey: ['permissions', 'me'],
    queryFn: () => permissionsApi.me(),
    staleTime: 60_000,
  });

  const allowedSet = useMemo(() => {
    if (!perms) return new Set();
    if (perms.is_admin) return new Set(TABS.map((t) => t.module));
    return new Set(perms.modules || []);
  }, [perms]);
  const tabs = TABS.filter((t) => allowedSet.has(t.module));

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-4 border-b border-surface-border dark:border-surface-dark-border">
        <h1 className="text-lg font-semibold mb-3">ACD</h1>
        <nav className="flex gap-1 -mb-px overflow-x-auto">
          {tabs.map((t) => t.disabled ? (
            <span
              key={t.to}
              title={`${t.label} — ${t.phase}`}
              className="px-3 py-2 text-sm rounded-t-thin text-surface-muted dark:text-surface-dark-muted cursor-not-allowed select-none whitespace-nowrap"
            >
              {t.label} <span className="text-xs">·</span> <span className="text-xs">{t.phase}</span>
            </span>
          ) : (
            <NavLink
              key={t.to}
              to={t.to}
              className={({ isActive }) =>
                'px-3 py-2 text-sm rounded-t-thin border-b-2 -mb-px whitespace-nowrap ' +
                (isActive
                  ? 'border-continuum-500 text-continuum-700 dark:text-continuum-200 font-medium'
                  : 'border-transparent text-surface-text dark:text-surface-dark-text hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle')
              }
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
      </div>
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
